<template>
  <div
    v-if="loading"
    class="skeleton flex w-full items-center rounded-md border h-10 px-4 focus-within:border-secondary-500 focus-within:shadow-[0px_0px_4px_theme('colors.primary.500')]"
  />
  <div
    v-else
    class="flex w-full items-center rounded-md border px-4 focus-within:border-secondary-500 focus-within:shadow-[0px_0px_4px_theme('colors.primary.500')]"
    :class="[
      size === 'xs' ? 'py-2' : '',
      size === 'small' ? 'py-2.5' : '',
      size === 'medium' ? 'py-4' : '',
      size === 'large' ? 'py-4' : '',
      disabled
        ? 'border-neutral-400 bg-neutral-50'
        : 'border-neutral-300 bg-neutral-0 hover:border-secondary-500 active:border-secondary-500'
    ]"
  >
    <div class="mr-3 flex items-center empty:hidden">
      <slot />
    </div>

    <input
      :id="labelId || undefined"
      ref="inputElement"
      :value="modelValue"
      class="w-full border-none p-0 leading-3 text-neutral-800 outline-none transition-colors placeholder:text-neutral-300 focus:ring-0"
      :class="[
        size === 'xs' || size === 'small' ? 'h-4 text-xs' : '',
        size === 'medium' ? 'text-sm' : '',
        size === 'large' ? 'text-base' : '',
        disabled ? 'bg-neutral-50 text-neutral-600' : ''
      ]"
      :type="type"
      :placeholder="placeholder"
      :name="name"
      :disabled="disabled"
      :required="required"
      :min="min"
      :max="max"
      :minlength="minlength"
      :maxlength="maxlength"
      :pattern="pattern"
      :step="step"
      :tabindex="tabindex"
      :autocomplete="autocomplete"
      @input="onInput"
    />

    <div class="ml-3 mr-2 flex items-center empty:hidden">
      <slot name="clear" :value="modelValue" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { inject, ref } from 'vue';
import { FieldLabelIdInjectionKey } from './InfuseFieldInjection';
import { autoResetRef } from '@vueuse/core';

const emit = defineEmits(['update:modelValue']);

interface Props {
  modelValue?: string | number;
  type?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'number' | 'search' | 'password' | 'file' | 'datetime-local';
  size?: 'xs' | 'small' | 'medium' | 'large';
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  minlength?: number;
  maxlength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  step?: number | 'any';
  tabindex?: number;
  autocomplete?: string;
  loading?: boolean;
}

withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  size: 'medium',
  placeholder: '',
  name: '',
  required: false,
  minlength: undefined,
  maxlength: undefined,
  min: undefined,
  max: undefined,
  pattern: undefined,
  step: undefined,
  tabindex: undefined,
  autocomplete: undefined,
  loading: false
});

const focus = () => {
  inputElement.value?.focus();
};

defineExpose({ focus });

const labelId = inject(FieldLabelIdInjectionKey, null);

const inputElement = ref<InstanceType<typeof HTMLInputElement> | null>();

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
}
</script>
